/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useAsyncSelect.js
 *
 * Purpose: Builds paginated, searchable "load options" functions for async select
 *     dropdowns backed by react-query.
 *
 * Scope: entity list loading for async select fields (e.g. assays, turnaround) in the
 *     customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Defines an internal loader that fetches a page of entities via axios, caches the
 *     result through react-query's queryClient, and maps records into {label, value} pairs for
 *     an async-paginate select component. Special-cases the Turnaround entity by prepending a
 *     synthetic "Standard" option to every fetched page, gated off for required fields (whose
 *     null id would fail the backend's required validation). The exported hook merges caller
 *     request params through useRequestParams, wraps the loader in useCallback, and returns
 *     query state alongside pagination props (debounceTimeout, additional, maxMenuHeight) ready
 *     to spread onto the select component.
 *
 * Exports: useAsyncSelect hook.
 */

import qs from 'qs';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { EntityType, QueryKeys } from 'shared/constants/entityTypes';
import { DEFAULT_PAGE_SIZE } from 'shared/constants/misc';
import { axiosinstance } from 'shared/utils/api';
import { noop } from 'shared/utils/misc';
import { useRequestParams } from '../api/misc';

const appendStandardTurnaround = (res = {}) => ({
  ...res,
  data: [{ id: null, name: 'Standard' }, ...res.data],
});

function _loadEntityOptions(entityName, labelFn, valueFn, appendStandard) {
  let queryState = {};
  const queryClient = useQueryClient();

  async function loadOptions(params = {}) {
    const queryKey = [entityName, 'options', params];
    const queryFn = async () => {
      const { data: res } = await axiosinstance.get(
        `/v1/${entityName.toLowerCase()}`,
        {
          params,
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: 'repeat' }),
        },
      );
      return res;
    };
    let res = await queryClient.fetchQuery(queryKey, queryFn);
    // Turnaround needs a special handling where we append a "Standard"
    // option as default — but not for required fields, where its null id
    // would fail the backend's required validation on submit. Applied
    // outside the cached queryFn so required and optional fields can
    // share a cache entry holding the raw server response.
    if (entityName === QueryKeys[EntityType.TURNAROUND] && appendStandard) {
      res = appendStandardTurnaround(res);
    }
    const {
      data: options = [],
      total_pages: total,
      page_number: current,
    } = res;
    queryState = queryClient.getQueryState(queryKey);

    return {
      options: options.map((obj) => ({
        label: labelFn(obj),
        value: valueFn(obj),
      })),
      hasMore: current < total,
      additional: { page: params.page + 1 },
    };
  }

  return [loadOptions, queryState];
}

export function useAsyncSelect({
  queryKey = '',
  reqParams = {},
  getOptionLabel = noop,
  getOptionValue = noop,
  required = false,
} = {}) {
  const params = useRequestParams(reqParams);
  const [fetchEntity, queryState] = _loadEntityOptions(
    queryKey,
    getOptionLabel,
    getOptionValue,
    !required,
  );
  const loadOptions = useCallback(
    async (search, _loadedOptions, { page }) =>
      await fetchEntity({ ...params, search, page }),
    [fetchEntity, params],
  );

  return {
    ...queryState,
    loadOptions,
    debounceTimeout: 600,
    additional: { page: 1 },
    maxMenuHeight: DEFAULT_PAGE_SIZE * 10,
  };
}
